import React, { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import SimproLogoBlue from 'icons/simpro-logo-blue.svg';
import SimproLogoWhite from 'icons/simpro-logo-white.svg';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import {
  useDestroySimproCompanyIntegrationMutation,
  useUpdateSimproCompanyIntegrationMutation,
} from 'components/hooks/simproCompanyIntegrationMutations';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import Glimmer from 'components/application/Glimmer';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import EditSimproIntegrationModal from 'components/settings/externalIntegrationConfiguration/components/EditSimproIntegrationModal';
import IntegrateWithSimproModal from 'components/settings/externalIntegrationConfiguration/components/IntegrateWithSimproModal';
import UnlinkSimproIntegrationModal from 'components/settings/externalIntegrationConfiguration/components/UnlinkSimproIntegrationModal';

import { formatDate } from 'components/helpers/dates';
import { formatData } from 'components/helpers/reactQuery';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function SimproIntegrationConfiguration({
  integratingSimproCompanyIntegrationId,
  seedSimproCompanyIntegrations,
  seedSimproProviderConfigurations,
}) {
  const [isIntegrateWithSimproModalOpen, setIsIntegrateWithSimproModalOpen] =
    useState(false);

  const { t: i18nTCommon } = useTranslation('common');

  // query function not required, provided initial data, then data updated via mutation
  const { data: domainSimproCompanyIntegrations } = useQuery({
    queryKey: ['integrations', 'simproCompanyIntegrations'],
    select: (data) =>
      formatData({
        data,
        resourceTypes: [
          'simproIdentity',
          'simproProviderConfiguration',
          'user',
        ],
      }),
    initialData: seedSimproCompanyIntegrations,
    enabled: false,
  });

  const integratedSimproCompanyIdsBySimproProviderConfigurationId =
    domainSimproCompanyIntegrations.data.reduce(
      (acc, simproCompanyIntegration) => {
        const simproIdentity =
          domainSimproCompanyIntegrations.included.simproIdentity[
            simproCompanyIntegration.relationships.simproIdentity.data.id
          ];

        const simproProviderConfigurationId =
          simproIdentity.relationships.simproProviderConfiguration.data.id;

        acc[simproProviderConfigurationId] ??= [];
        acc[simproProviderConfigurationId].push(
          simproCompanyIntegration.attributes.simproCompanyId,
        );

        return acc;
      },
      {},
    );

  useEffect(() => {
    const url = new URL(window.location.href);

    if (url.searchParams.has('integrating_simpro_company_integration_id')) {
      url.searchParams.delete('integrating_simpro_company_integration_id');
      window.history.replaceState(null, '', url.href);
    }
  }, []);

  return (
    <>
      <div>
        <h3 className='tw-mb-2 tw-mt-0 tw-text-m tw-font-semibold tw-tracking-auto'>
          Simpro
        </h3>
        {domainSimproCompanyIntegrations.data.map(
          (simproCompanyIntegration) => {
            const simproIdentity =
              domainSimproCompanyIntegrations.included.simproIdentity[
                simproCompanyIntegration.relationships.simproIdentity.data.id
              ];
            const user =
              domainSimproCompanyIntegrations.included.user[
                simproIdentity.relationships.user.data.id
              ];
            const simproProviderConfiguration =
              domainSimproCompanyIntegrations.included
                .simproProviderConfiguration[
                simproIdentity.relationships.simproProviderConfiguration.data.id
              ];
            const integratedSimproCompanyIds =
              integratedSimproCompanyIdsBySimproProviderConfigurationId[
                simproProviderConfiguration.id
              ];

            return (
              <div className='tw-mb-4' key={simproCompanyIntegration.id}>
                <SimproCompanyIntegrationCard
                  integratedSimproCompanyIds={integratedSimproCompanyIds}
                  isSimproIntegrationInitiallyEditable={
                    integratingSimproCompanyIntegrationId ===
                    simproCompanyIntegration.id
                  }
                  simproCompanyIntegration={simproCompanyIntegration}
                  simproProviderConfiguration={simproProviderConfiguration}
                  user={user}
                />
              </div>
            );
          },
        )}
        {seedSimproProviderConfigurations.data.length > 1 ?
          <button
            className='tw-apply-filled-button tw-apply-filled-button--on-light tw-apply-filled-button--md tw-apply-filled-button--simpro'
            onClick={() => setIsIntegrateWithSimproModalOpen(true)}
          >
            <SimproLogoWhite className='tw-mr-4.5' height='16px' width='16px' />
            <span>
              {i18nTCommon('integrations.integrateWith', {
                provider: 'Simpro',
              })}
            </span>
            <sup>&reg;</sup>
          </button>
        : <a
            className='tw-apply-filled-button tw-apply-filled-button--on-light tw-apply-filled-button--md tw-apply-filled-button--simpro'
            data-method='post'
            href={`/users/auth/simpro?simpro_provider_configuration_uuid=${seedSimproProviderConfigurations.data[0].attributes.uuid}`}
            rel='nofollow'
          >
            <SimproLogoWhite className='tw-mr-4.5' height='16px' width='16px' />
            <span>
              {i18nTCommon('integrations.integrateWith', {
                provider: 'Simpro',
              })}
            </span>
            <sup>&reg;</sup>
          </a>
        }
      </div>
      {seedSimproProviderConfigurations.data.length > 1 &&
        isIntegrateWithSimproModalOpen && (
          <IntegrateWithSimproModal
            onClose={() => setIsIntegrateWithSimproModalOpen(false)}
            simproProviderConfigurations={seedSimproProviderConfigurations.data}
          />
        )}
    </>
  );
}

SimproIntegrationConfiguration.propTypes = {
  integratingSimproCompanyIntegrationId: PropTypes.string,
  seedSimproCompanyIntegrations: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('simproCompanyIntegration'))
      .isRequired,
    included: PropTypes.array,
  }).isRequired,
  seedSimproProviderConfigurations: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('simproProviderConfiguration'))
      .isRequired,
  }).isRequired,
};

function SimproCompanyIntegrationCard({
  integratedSimproCompanyIds,
  isSimproIntegrationInitiallyEditable,
  simproCompanyIntegration,
  simproProviderConfiguration,
  user,
}) {
  const {
    division: {
      attributes: { pluralRAMSName, singularRAMSName },
    },
    user: currentUser,
  } = useCurrentActor();

  const updateSimproCompanyIntegrationMutation =
    useUpdateSimproCompanyIntegrationMutation();

  const destroySimproCompanyIntegrationMutation =
    useDestroySimproCompanyIntegrationMutation();

  const [
    isEditSimproIntegrationModalOpen,
    setIsEditSimproIntegrationModalOpen,
  ] = useState(isSimproIntegrationInitiallyEditable);

  const [
    isUnlinkSimproIntegrationModalOpen,
    setIsUnlinkSimproIntegrationModalOpen,
  ] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'settings.externalIntegrationConfiguration.components.SimproIntegrationConfiguration',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const simproCompanyIntegrationSimproCompanyId =
    simproCompanyIntegration.attributes.simproCompanyId;

  const { data: domainSimproInfo, isSuccess: isDomainSimproInfoFetchSuccess } =
    useQuery({
      queryKey: [
        'simproCompanyIntegrations',
        simproCompanyIntegration.id,
        'simproInfo',
      ],
      queryFn: async () => {
        const response = await axios.get(
          `/simpro_company_integrations/${simproCompanyIntegration.id}/simpro_info`,
        );

        return response.data;
      },
      placeholderData: { data: null, meta: {} },
      refetchInterval: (data) =>
        !data || ['completed', 'failed'].includes(data.meta.requestStatus) ?
          false
        : 1000,
      refetchIntervalInBackground: true,
    });

  const {
    data: domainSimproCompany,
    isSuccess: isDomainSimproCompanyFetchSuccess,
  } = useQuery({
    queryKey: [
      'simproCompanyIntegrations',
      simproCompanyIntegration.id,
      'simproCompanies',
      simproCompanyIntegrationSimproCompanyId,
    ],
    queryFn: async () => {
      const response = await axios.get(
        `/simpro_company_integrations/${simproCompanyIntegration.id}/simpro_companies/${simproCompanyIntegrationSimproCompanyId}`,
      );

      return response.data;
    },
    placeholderData: { data: null, meta: {} },
    refetchInterval: (data) =>
      !data || ['completed', 'failed'].includes(data.meta.requestStatus) ?
        false
      : 1000,
    refetchIntervalInBackground: true,
  });

  const handleUpdate = ({ simproCompanyId }) => {
    updateSimproCompanyIntegrationMutation.mutate({
      id: simproCompanyIntegration.id,
      simproCompanyIntegrationParams: {
        simpro_company_id: simproCompanyId,
      },
    });

    setIsEditSimproIntegrationModalOpen(false);
  };

  const handleUnlink = () => {
    destroySimproCompanyIntegrationMutation.mutate({
      id: simproCompanyIntegration.id,
    });

    setIsUnlinkSimproIntegrationModalOpen(false);
  };

  const { simproJobIntegrationConfigurationCount, simproJobIntegrationCount } =
    simproCompanyIntegration.meta;

  const isSimproInfoFetchRequestCompleted =
    isDomainSimproInfoFetchSuccess &&
    domainSimproInfo.meta.requestStatus === 'completed';

  const isSimproInfoFetchRequestFailed =
    isDomainSimproInfoFetchSuccess &&
    domainSimproInfo.meta.requestStatus === 'failed';

  const isSimproCompanyFetchRequestCompleted =
    isDomainSimproCompanyFetchSuccess &&
    domainSimproCompany.meta.requestStatus === 'completed';

  const isSimproCompanyFetchRequestFailed =
    isDomainSimproCompanyFetchSuccess &&
    domainSimproCompany.meta.requestStatus === 'failed';

  const isDetailsFetchRequestCompleted =
    isSimproInfoFetchRequestCompleted && isSimproCompanyFetchRequestCompleted;

  const anySimproFetchRequestsFailed =
    isSimproInfoFetchRequestFailed || isSimproCompanyFetchRequestFailed;

  const isEditButtonDisabled =
    isDetailsFetchRequestCompleted &&
    (currentUser.id !== user.id ||
      simproJobIntegrationConfigurationCount > 0 ||
      simproJobIntegrationCount > 0);

  const isReauthenticateButtonDisabled =
    anySimproFetchRequestsFailed && currentUser.id !== user.id;

  return (
    <>
      <p className='tw-mb-4'>
        {i18nT('companyIntegrationText', {
          date: formatDate({
            date: simproCompanyIntegration.attributes.createdAt,
            format: 'dd MMM yyyy',
          }),
          displayName: personDisplayName({
            attributes: user.attributes,
          }),
          interpolation: { escapeValue: false },
        })}
      </p>
      <div className='fw-360 tw-flex tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-p-4'>
        <div className='tw-pr-3'>
          <div className='tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full'>
            <SimproLogoBlue height='40px' width='40px' />
          </div>
        </div>
        <div className='tw-min-w-0'>
          {isDetailsFetchRequestCompleted ?
            <>
              <div className='tw-mb-4 tw-truncate'>
                <span className='tw-font-medium tw-text-grey-900'>
                  Simpro company:
                </span>{' '}
                <span>{domainSimproCompany.data.attributes.name}</span>
              </div>
              <div className='tw-flex tw-gap-2'>
                <div className='tooltip-parent'>
                  {isEditButtonDisabled && (
                    <Tooltip
                      placement='top'
                      tooltip={(() => {
                        if (currentUser.id !== user.id)
                          return i18nT(
                            'edit.disabledTooltipText.notTheUserWhoSetUpIntegration',
                          );
                        else if (simproJobIntegrationConfigurationCount > 0)
                          return i18nT(
                            'edit.disabledTooltipText.projectLinksConfigured',
                          );
                        else if (simproJobIntegrationCount > 0)
                          return i18nT(
                            'edit.disabledTooltipText.projectsLinked',
                            {
                              count: simproJobIntegrationCount,
                              interpolation: { escapeValue: false },
                              ramsName:
                                simproJobIntegrationCount === 1 ?
                                  singularRAMSName
                                : pluralRAMSName,
                            },
                          );
                      })()}
                      trigger='hover'
                    />
                  )}
                  <OutlinedButton
                    color='grey'
                    disabled={isEditButtonDisabled}
                    onClick={() => setIsEditSimproIntegrationModalOpen(true)}
                    size='sm'
                  >
                    {i18nTCommon('shared.edit')}
                  </OutlinedButton>
                </div>
                <OutlinedButton
                  color='red'
                  onClick={() => setIsUnlinkSimproIntegrationModalOpen(true)}
                  size='sm'
                >
                  {i18nTCommon('shared.unlink')}
                </OutlinedButton>
              </div>
            </>
          : anySimproFetchRequestsFailed ?
            <>
              <Trans
                components={{
                  div: (
                    <div className='tw-mb-1 tw-font-semibold tw-text-red-600' />
                  ),
                  p: (
                    <div className='tw-mb-4 tw-text-s tw-font-normal tw-tracking-wide' />
                  ),
                }}
                i18nKey={`settings.externalIntegrationConfiguration.components.SimproIntegrationConfiguration.${(() =>
                  isSimproInfoFetchRequestFailed ?
                    (() => {
                      switch (domainSimproInfo.meta.errorReason) {
                        case 'not_found':
                          return 'simproInfoNotFoundError';
                        case 'unauthorized':
                          return 'simproInfoUnauthorizedError';
                        case 'unknown':
                          return 'simproInfoUnknownError';
                        default:
                          return null;
                      }
                    })()
                  : isSimproCompanyFetchRequestFailed ?
                    (() => {
                      switch (domainSimproCompany.meta.errorReason) {
                        case 'not_found':
                          return 'simproCompanyNotFoundError';
                        case 'unauthorized':
                          return 'simproCompanyUnauthorizedError';
                        case 'unknown':
                          return 'simproCompanyUnknownError';
                        default:
                          return null;
                      }
                    })()
                  : null)()}`}
                ns='components'
              />
              <div className='tw-flex tw-gap-2'>
                {(domainSimproInfo.meta.errorReason === 'unauthorized' ||
                  domainSimproCompany.meta.errorReason === 'unauthorized') && (
                  <div className='tooltip-parent'>
                    {isReauthenticateButtonDisabled && (
                      <Tooltip
                        placement='top'
                        tooltip={i18nT('reauthenticate.disabledTooltipText')}
                        trigger='hover'
                      />
                    )}
                    <OutlinedButton
                      color='grey'
                      data-method='post'
                      disabled={isReauthenticateButtonDisabled}
                      href={`/users/auth/simpro?simpro_provider_configuration_uuid=${simproProviderConfiguration.attributes.uuid}&mode=refresh_tokens`}
                      rel='nofollow'
                      size='sm'
                    >
                      {i18nTCommon('shared.reauthenticate')}
                    </OutlinedButton>
                  </div>
                )}
                <div className='tooltip-parent'>
                  <OutlinedButton
                    color='red'
                    onClick={() => setIsUnlinkSimproIntegrationModalOpen(true)}
                    size='sm'
                  >
                    {i18nTCommon('shared.unlink')}
                  </OutlinedButton>
                </div>
              </div>
            </>
          : <>
              <div className='tw-text-grey-900'>
                {i18nTCommon('integrations.integrationSetUp', {
                  provider: 'Simpro',
                })}
              </div>
              <div className='tw-text-s tw-font-normal tw-tracking-wide'>
                <span>
                  {i18nTCommon('shared.loading...')} <Glimmer />
                </span>
              </div>
            </>
          }
        </div>
      </div>
      {isDetailsFetchRequestCompleted && isEditSimproIntegrationModalOpen && (
        <EditSimproIntegrationModal
          integratedSimproCompanyIds={integratedSimproCompanyIds}
          isSaving={updateSimproCompanyIntegrationMutation.isLoading}
          isSimproMultiCompany={domainSimproInfo.data.attributes.multiCompany}
          onClose={() => setIsEditSimproIntegrationModalOpen(false)}
          onSave={handleUpdate}
          simproCompany={domainSimproCompany.data}
          simproCompanyIntegration={simproCompanyIntegration}
        />
      )}
      {(isDetailsFetchRequestCompleted || anySimproFetchRequestsFailed) &&
        isUnlinkSimproIntegrationModalOpen && (
          <UnlinkSimproIntegrationModal
            isUnlinking={destroySimproCompanyIntegrationMutation.isLoading}
            onClose={() => setIsUnlinkSimproIntegrationModalOpen(false)}
            onUnlink={handleUnlink}
            simproJobIntegrationConfigurationCount={
              simproJobIntegrationConfigurationCount
            }
            simproJobIntegrationCount={simproJobIntegrationCount}
          />
        )}
    </>
  );
}

SimproCompanyIntegrationCard.propTypes = {
  integratedSimproCompanyIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  isSimproIntegrationInitiallyEditable: PropTypes.bool.isRequired,
  simproCompanyIntegration: resourceShape('simproCompanyIntegration')
    .isRequired,
  simproProviderConfiguration: resourceShape('simproProviderConfiguration')
    .isRequired,
  user: resourceShape('user').isRequired,
};
